/* ============================================================== */
/*                                                                */
/*   PatientHistoryV4  ·  sequence ของหน้า Patient History Viewer  */
/*   (HIS V4 · อ่านอย่างเดียว · ไม่มี action บันทึก/ลบในหน้านี้)      */
/*                                                                */
/*   practice เดียวกับ ExamV4 / ResultSummaryV4:                     */
/*     data    -> state.PatientHistoryV4Sequence                      */
/*     loading -> state.buttonLoadCheck["CardPatientHistoryV4_<ACTION>"] */
/*     error   -> state.errorMessage["CardPatientHistoryV4"]          */
/*                                                                */
/*   API ที่ใช้ (verify จาก issara-sdk + HIS V3):                     */
/*     GET /apis/DPO/encounter-history/?patient&start&end&division    */
/*         &doctor&encounter_type&limit&offset                         */
/*         (EncounterHistoryList_apps_DPO, V3 ส่ง params แบบเดียวกัน  */
/*          ใน HISV3/PHV/CardPHVWrapper.tsx:961-1001)                 */
/*     รายละเอียด 1 encounter ใช้ endpoint เดียวกับหน้าตรวจรักษา:     */
/*     GET /apis/core/encounter/{id}/medical-record/  -> emr id       */
/*     GET /apis/DPO/medical_record/emr/{emr}/                        */
/*     GET /apis/DPO/medical_record/{emr}/diagnosis/                  */
/*     GET /apis/core/triage/encounter/{encounter}/                   */
/*     GET /apis/PTM/vital-sign-value/                                */
/*     GET /apis/LAB/central-lab-result-by-encounter/?encounter=      */
/*     GET /apis/IME/list_result/?encounter=                          */
/*     GET /apis/TPD/medication-record-list-summary/?encounter=       */
/*     GET /apis/TRT/treatment-order/?encounter_id=                   */
/*     GET /apis/PTM/nurse-note-item/?encounter=                      */
/*     GET /apis/DPO/medical_record/gallery/{emr}/                    */
/*     GET /apis/core/scanned-document/?encounter=                    */
/*     GET /apis/ADR/adverse-reaction/?patient= (ประวัติแพ้)          */
/*     GET /apis/DPO/problem-list/?patient=  (โรคประจำตัว)            */
/*                                                                */
/* ============================================================== */

import DoctorCertificatesList from "issara-sdk/apis/DoctorCertificatesList_apps_DPO";
import DoctorCertificatesDetail from "issara-sdk/apis/DoctorCertificatesDetail_apps_DPO";
import DoctorCertificatesTypeList from "issara-sdk/apis/DoctorCertificatesTypeList_apps_DPO";
import FormDataActionLogAllList from "issara-sdk/apis/FormDataActionLogAllList_apps_PTM";
import FormDataDetail from "issara-sdk/apis/FormDataDetail_apps_PTM";
import EncounterHistoryList from "issara-sdk/apis/EncounterHistoryList_apps_DPO";
import EncounterMedicalRecordList from "issara-sdk/apis/EncounterMedicalRecordList_core";
import PatientEMRList from "issara-sdk/apis/PatientEMRList_apps_DPO";
import DiagnosisMedicalRecordDetail from "issara-sdk/apis/DiagnosisMedicalRecordDetail_apps_DPO";
import TriageFromEncounter from "issara-sdk/apis/TriageFromEncounter_core";
import VitalSignValueList from "issara-sdk/apis/VitalSignValueList_apps_PTM";
import CentralLabResultEncounterList from "issara-sdk/apis/CentralLabResultEncounterList_apps_LAB";
import ImagingOrderResultList from "issara-sdk/apis/ImagingOrderResultList_apps_IME";
import DrugOrderItemList from "issara-sdk/apis/DrugOrderItemList_apps_TPD";
import MedicationRecordListSummaryView from "issara-sdk/apis/MedicationRecordListSummaryView_apps_TPD";
import TreatmentOrderList from "issara-sdk/apis/TreatmentOrderList_apps_TRT";
import NurseNoteItemList from "issara-sdk/apis/NurseNoteItemList_apps_PTM";
import MedicalRecordGalleryList from "issara-sdk/apis/MedicalRecordGalleryList_apps_DPO";
import ScannedDocumentList from "issara-sdk/apis/ScannedDocumentList_core";
import MultidisciplinaryItemList from "issara-sdk/apis/MultidisciplinaryItemList_apps_PTM";
import AdverseReactionList from "issara-sdk/apis/AdverseReactionList_apps_ADR";
import ProblemListList from "issara-sdk/apis/ProblemListList_apps_DPO";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  apiErrorTextOf,
  markButtonLoadCheck,
  seqBtnKey,
  seqErrorKey,
  writeSequenceState,
  type BLC_STATUS_TYPE,
  type SeqMeta,
} from "./sequenceStateV4";

import {
  PARTIAL_FAILED,
  HistoryDetailPayloads,
  HistoryFilter,
  HistoryIds,
  HistoryV4Slice,
  hasBadDate,
  historyDateError,
  beDateOnlyText,
  emptyHistoryV4Slice,
  historyDetailFromApiPayloads,
  historyEncounterListParams,
  historyEncountersFromApi,
  normalizeHistoryV4Slice,
  sortEncounters,
} from "../../patient/historyModelV4";
import { allergyListParams, itemsOf, problemListParams, asNumber, asText } from "../../patient/examModelV4";

/* ============================================================== */
/*   SECTION 1 · TYPE-DEFINING CONSTANTS                           */
/* ============================================================== */

export const SEQUENCE_NAME = "PatientHistoryV4" as const;

export const CARD_NAME = "CardPatientHistoryV4";

export const ACTIONS = {
  LOAD: "LOAD",
  SET_FILTER: "SET_FILTER",
  SELECT: "SELECT",
  LOAD_DETAIL: "LOAD_DETAIL",
} as const;

/** key ใน state.buttonLoadCheck เช่น CardPatientHistoryV4_LOAD */
export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const HISTORY_SLICE_KEY = "PatientHistoryV4Sequence";

/* ============================================================== */
/*   SECTION 2 · STATE                                             */
/* ============================================================== */

export type State = Partial<{
  PatientHistoryV4Sequence: Partial<HistoryV4Slice> | null;
}>;

export const StateInitial: State = { PatientHistoryV4Sequence: null };

export type Event = { message: "RunSequence"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export const DataInitial: Data = {};

export type Props = {};

/* ============================================================== */
/*   SECTION 3 · PARAMS TYPE                                       */
/* ============================================================== */

type SequenceName = typeof SEQUENCE_NAME;
type Actions = typeof ACTIONS;
export type ActionName = Actions[keyof Actions];

export type HistoryIdsInput = Partial<HistoryIds> & { ids?: HistoryIds };

export type SeqState = BaseSeqState<SequenceName> &
  HistoryIdsInput & {
    card?: string;
    filter?: Partial<HistoryFilter>;
    encounterId?: unknown;
  };

export type ActionPayloadMap = {
  [ACTIONS.LOAD]: HistoryIdsInput & { filter?: Partial<HistoryFilter>; offset?: number };
  [ACTIONS.SET_FILTER]: HistoryIdsInput & { filter: Partial<HistoryFilter> };
  [ACTIONS.SELECT]: HistoryIdsInput & { encounterId: number };
  [ACTIONS.LOAD_DETAIL]: HistoryIdsInput & { encounterId: number; emrId?: number };
};

type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];
export type HistoryRunSequence = RunSequence;

export type HistoryRunParams = {
  [K in ActionName]: {
    sequence: typeof SEQUENCE_NAME;
    action: K;
    card?: string;
    restart?: boolean;
    clear?: boolean;
  } & ActionPayloadMap[K];
}[ActionName];

export type HistoryRunParamsLoose = {
  sequence: string;
  action: string;
  card?: string;
  restart?: boolean;
  clear?: boolean;
  [key: string]: unknown;
};

export type HistoryRunSequenceProp = (params: HistoryRunParams | HistoryRunParamsLoose) => void;

export type SetProp = ActionTypes["SetProp"];
export type OnEvent = ActionTypes["OnEvent"];

type Ctx<A extends ActionName> = HistoryIdsInput & {
  ids: HistoryIds;
  card?: string;
  action?: A;
  filter?: Partial<HistoryFilter>;
  encounterId?: number;
  offset?: number;
};

type CombinedState = PickedState & State;

export type Handler<P = unknown, R = void> = ActionHandler<CombinedState, Event, Data, P, R>;

type Ctl = {
  getState: () => CombinedState & Record<string, any>;
  setState: (state: any, callback?: () => void) => any;
  handleEvent: (event: Event) => void;
  apiToken?: string;
  data?: Data;
};

/* ============================================================== */
/*   SECTION 4 · INTERNAL HELPERS                                  */
/* ============================================================== */

const extraOf = (controller: Ctl) => ({
  division: controller.data?.division,
  device: controller.data?.device,
});

type ApiTuple = [unknown, unknown, unknown];

const errorMessageOf = (result: ApiTuple): string | null =>
  apiErrorTextOf(result, { fallbackText: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" });

const dataOf = (result: ApiTuple): unknown => result[0];

const sliceOf = (controller: Ctl): HistoryV4Slice =>
  normalizeHistoryV4Slice(controller.getState().PatientHistoryV4Sequence);

type SliceMeta = { card?: string; action?: string };

const metaOf = (meta: SliceMeta): SeqMeta => ({
  sequence: SEQUENCE_NAME,
  card: meta.card ?? CARD_NAME,
  action: meta.action,
});

export const historyBtnKey = (meta: SliceMeta): string => seqBtnKey(metaOf(meta));
export const historyErrorKey = (meta: SliceMeta): string => seqErrorKey(metaOf(meta));

type WritePatch = Partial<HistoryV4Slice> & {
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
};

const writeSlice = (controller: Ctl, meta: SliceMeta, patch: WritePatch): any =>
  writeSequenceState<HistoryV4Slice>(
    controller,
    metaOf(meta),
    HISTORY_SLICE_KEY,
    sliceOf(controller),
    { ...patch, sequenceIndex: "Action" },
    emptyHistoryV4Slice,
  );

const setBtn = (controller: Ctl, metas: SliceMeta[], status: BLC_STATUS_TYPE): void =>
  markButtonLoadCheck(controller, metas.map(metaOf), status);

const numberOrUndefined = (value: unknown): number | undefined => asNumber(value);

/** "วันนี้"รูปแบบ พ.ศ. (เช่น 25691030) — test/app override.get override ได้ผ่าน state.hisV4TodayBE */
const todayNumOf = (controller: Ctl): number => {
  const app = controller.getState() as { hisV4TodayBE?: unknown };
  const override = numberOrUndefined(app.hisV4TodayBE);
  if (override !== undefined) return override;

  const now = new Date();
  return (now.getFullYear() + 543) * 10000 + (now.getMonth() + 1) * 100 + now.getDate();
};

const readIds = (controller: Ctl, params: HistoryIdsInput): HistoryIds => {
  const slice = sliceOf(controller);
  const app = controller.getState() as {
    selectedEncounter?: { id?: unknown; patient_id?: unknown };
    selectedPatient?: { id?: unknown };
    division?: unknown;
  };
  const fromParams: Partial<HistoryIds> = params.ids ?? params;

  return {
    patientId:
      fromParams.patientId ??
      slice.ids.patientId ??
      numberOrUndefined(app.selectedPatient?.id) ??
      numberOrUndefined(app.selectedEncounter?.patient_id),
    encounterId:
      fromParams.encounterId ??
      slice.ids.encounterId ??
      numberOrUndefined(app.selectedEncounter?.id) ??
      numberOrUndefined((params as { encounterId?: unknown }).encounterId),
    emrId: fromParams.emrId ?? slice.ids.emrId,
    divisionId: fromParams.divisionId,
    doctorId: fromParams.doctorId ?? slice.ids.doctorId,
  };
};

const filterOf = (
  controller: Ctl,
  params: HistoryIdsInput & { filter?: Partial<HistoryFilter> },
): HistoryFilter => {
  const slice = sliceOf(controller);
  const next = params.filter;
  if (next === undefined) return slice.filter;
  return { ...slice.filter, ...next };
};

/** emr ของ encounter (endpoint เดียวกับ ExamV4: /apis/core/encounter/{id}/medical-record/) */
const resolveEmrId = async (
  controller: Ctl,
  encounterId: number,
): Promise<{ emrId: number | undefined; error: string | null }> => {
  const result = await EncounterMedicalRecordList.list({
    pk: encounterId,
    apiToken: controller.apiToken,
  });
  const error = errorMessageOf(result as ApiTuple);
  if (error) return { emrId: undefined, error };

  const record = itemsOf(dataOf(result as ApiTuple))[0];
  if (record === undefined) return { emrId: undefined, error: null };

  return { emrId: numberOrUndefined(record.id), error: null };
};

/**
 * เรียก 1 endpoint แบบไม่ทำให้ทั้งชุดล้ม (หน้าอ่านอย่างเดียวต้องโชว์เท่าที่มี)
 * failed มาพร้อม errorText ของ backend (403/500) เพื่อเอามาแสดงใน errorMessage
 */
const softCall = async (
  call: () => Promise<unknown>,
): Promise<{ payload: unknown; failed: boolean; errorText: string | null }> => {
  try {
    const result = (await call()) as ApiTuple;
    const error = errorMessageOf(result);
    if (error) return { payload: PARTIAL_FAILED, failed: true, errorText: error };
    return { payload: dataOf(result), failed: false, errorText: null };
  } catch {
    return { payload: PARTIAL_FAILED, failed: true, errorText: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" };
  }
};

const requests = new WeakMap<object, { list: number; detail: number }>();
const requestState = (controller: Ctl) => {
  let value = requests.get(controller);
  if (!value) { value = { list: 0, detail: 0 }; requests.set(controller, value); }
  return value;
};

/* ============================================================== */
/*   SECTION 5 · READ                                              */
/* ============================================================== */

/**
 * รายการ encounter ย้อนหลังทั้งหมดของ patient (client เป็นผู้กรองช่วงวันที่/ประเภท/คำค้น)
 */
const LoadEncounters: Handler<Ctx<"LOAD">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const filter = filterOf(controller, params);

  if (!ids.patientId) {
    return writeSlice(controller, params, {
      ids,
      filter,
      encounters: [],
      total: 0,
      error: "ยังไม่ได้เลือกผู้รับบริการ (patient)",
    });
  }

  if (hasBadDate(filter)) return writeSlice(controller, params, { error: historyDateError(filter) });
  const offset = params.offset ?? 0;
  const request = requestState(controller);
  const token = ++request.list;
  const patientChanged = sliceOf(controller).ids.patientId !== ids.patientId;
  if (patientChanged) ++request.detail;
  writeSlice(controller, params, { ids, loading: true,
    ...(patientChanged ? { encounters: [], total: 0, detail: null, detailFor: null, selectedId: null, offset: 0, nextOffset: null } : {}) });
  const listResult = await EncounterHistoryList.list({
    params: historyEncounterListParams({
      ids,
      filter,
      todayNum: todayNumOf(controller),
      offset: params.offset ?? 0,
    }),
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const listError = errorMessageOf(listResult as ApiTuple);

  if (request.list !== token || sliceOf(controller).ids.patientId !== ids.patientId) return;

  if (listError) {
    /* a failed next page must not discard the rows already loaded; the user can retry */
    if (offset > 0) return writeSlice(controller, params, { ids, loading: false, error: listError });
    return writeSlice(controller, params, {
      ids,
      filter,
      encounters: [],
      total: 0,
      offset: 0,
      nextOffset: null,
      loading: false,
      detail: null,
      detailFor: null,
      error: listError,
    });
  }

  const page = historyEncountersFromApi(dataOf(listResult));
  const previous = offset > 0 ? sliceOf(controller).encounters : [];
  const encounters = sortEncounters([...previous.filter((row) => !page.some((item) => item.id === row.id)), ...page]);
  const selectedAvailable = encounters.some((row) => row.id === sliceOf(controller).selectedId && !row.isSecret);
  if (!selectedAvailable) ++request.detail;
  const payload = dataOf(listResult) as { total?: number; next?: string | null };
  const total = typeof payload?.total === "number" ? payload.total : encounters.length;
  const nextOffset = payload?.next ? Number(new URL(payload.next, "https://local.invalid").searchParams.get("offset")) : null;

  writeSlice(controller, params, {
    ids,
    filter,
    encounters,
    total,
    offset,
    nextOffset,
    loading: false,
    detailLoading: selectedAvailable ? sliceOf(controller).detailLoading : false,
    selectedId: encounters.some((row) => row.id === sliceOf(controller).selectedId) ? sliceOf(controller).selectedId : encounters[0]?.id ?? null,
    /* ชะลอ detail ของ encounter ที่เลือกไว้ก่อน (LOAD_DETAIL จะเรียกใหม่ตอน SELECT) */
    detail: encounters.some((row) => row.id === sliceOf(controller).detailFor && !row.isSecret)
      ? sliceOf(controller).detail
      : null,
    detailFor: encounters.some((row) => row.id === sliceOf(controller).detailFor && !row.isSecret)
      ? sliceOf(controller).detailFor
      : null,
    error: null,
  });
};

/** SET_FILTER/SELECT เขียน state อย่างเดียว ไม่เรียก API (filter เป็น client-side ตามแบบ 4d) */
const SetFilter: Handler<Ctx<"SET_FILTER">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const filter = filterOf(controller, params);
  return writeSlice(controller, params, { ids, filter, error: null });
};

const SelectEncounter: Handler<Ctx<"SELECT">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const encounterId = params.encounterId ?? ids.encounterId;

  if (encounterId === undefined) {
    return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือก encounter" });
  }

  ++requestState(controller).detail;
  return writeSlice(controller, params, {
    ids: { ...ids, encounterId },
    selectedId: encounterId,
    detail: null,
    detailFor: null,
    error: null,
  });
};

/**
 * รายละเอียด 1 encounter (อ่านอย่างเดียว)
 * endpoint ที่ล้มทีละตัว ไม่ทำให้ทั้งหน้าล้ม — เก็บเป็น partialErrors ของ slice
 */
const LoadDetail: Handler<Ctx<"LOAD_DETAIL">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const encounterId = params.encounterId ?? ids.encounterId;
  const slice = sliceOf(controller);

  if (encounterId === undefined) {
    return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือก encounter" });
  }

  if (slice.ids.patientId !== undefined && ids.patientId !== slice.ids.patientId)
    return writeSlice(controller, params, { detail: null, detailFor: null, detailLoading: false });
  const encounter = slice.encounters.find((row) => row.id === encounterId);
  if (encounter === undefined) {
    return writeSlice(controller, params, {
      ids,
      error: "ไม่พบ encounterนี้ในรายการที่โหลดไว้ (กรุณา Search ใหม่)",
    });
  }

  const request = requestState(controller);
  const token = ++request.detail;
  if (encounter.isSecret) return writeSlice(controller, params, {
    detail: null, detailFor: null, selectedId: encounterId, detailLoading: false, error: null,
  });
  writeSlice(controller, params, { ids: { ...ids, encounterId }, selectedId: encounterId,
    detail: null, detailFor: null, detailLoading: true });
  const stale = () => request.detail !== token || sliceOf(controller).ids.patientId !== ids.patientId ||
    sliceOf(controller).selectedId !== encounterId ||
    !sliceOf(controller).encounters.some((row) => row.id === encounterId && !row.isSecret);

  const emr = encounter.emrId
    ? { emrId: encounter.emrId, error: null }
    : await resolveEmrId(controller, encounterId);

  if (stale()) return;

  const emrId = emr.emrId;
  const patientId = ids.patientId;

  const [
    emrPayload,
    diagnosisPayload,
    triagePayload,
    vitalPayload,
    labPayload,
    imagingPayload,
    medicationPayload,
    administrationPayload,
    procedurePayload,
    nursePayload,
    consultPayload,
    galleryPayload,
    scannedPayload,
    certificatePayload,
    certificateTypePayload,
    formCertificatePayload,
    allergyPayload,
    problemPayload,
  ] = await Promise.all([
    emrId ? softCall(() =>
      PatientEMRList.get({
        emr: emrId,
        params: { auto_checkin: false },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ) : Promise.resolve({ payload: emr.error ? PARTIAL_FAILED : undefined, failed: !!emr.error, errorText: emr.error }),
    emrId ? softCall(() =>
      DiagnosisMedicalRecordDetail.retrieve({
        pk: emrId,
        params: {},
        apiToken: controller.apiToken,
      }),
    ) : Promise.resolve({ payload: emr.error ? PARTIAL_FAILED : undefined, failed: !!emr.error, errorText: emr.error }),
    softCall(() => TriageFromEncounter.retrieve({ pk: encounterId, apiToken: controller.apiToken })),
    softCall(() =>
      VitalSignValueList.list({
        params: { patient: patientId, encounter: encounterId, latest_measurement: true },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
    softCall(() =>
      CentralLabResultEncounterList.list({
        params: { encounter: encounterId },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
    softCall(() =>
      ImagingOrderResultList.list({
        params: { encounter: encounterId },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
    softCall(() => DrugOrderItemList.list({
      params: { encounter: encounterId, disable_pagination: true },
      apiToken: controller.apiToken, extra: extraOf(controller),
    })),
    emrId ? softCall(() =>
      MedicationRecordListSummaryView.get({
        /* contract of this view: `emr` (MedicalRecord id) is required -- with `encounter` only the view keeps a
           str and crashes (encounter.actions -> AttributeError), and an empty `date` crashes too
           (NoneType += timedelta). date is Buddhist era dd/mm/yyyy taken from the encounter row. */
        params: { emr: emrId, encounter: encounterId, date: beDateOnlyText(encounter) },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ) : Promise.resolve({ payload: emr.error ? PARTIAL_FAILED : undefined, failed: !!emr.error, errorText: emr.error }),
    softCall(() =>
      TreatmentOrderList.list({
        params: { encounter_id: encounterId, exclude_cancel: false },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
    softCall(() =>
      NurseNoteItemList.list({
        /* params เดียวกับหน้าบันทึกการพยาบาล (ExamV4.LoadNurse) */
        params: { encounter: encounterId, sort_descending: true },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
    emrId ? softCall(() =>
      MedicalRecordGalleryList.list({
        emr: emrId,
        params: {},
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ) : Promise.resolve({ payload: emr.error ? PARTIAL_FAILED : undefined, failed: !!emr.error, errorText: emr.error }),
    /* 86634 ข้อ 4: Multidisciplinary ของ Encounter นี้ (ดูย้อนหลังใน PHV) */
    softCall(() =>
      MultidisciplinaryItemList.list({
        params: { encounter: encounterId },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
    softCall(() =>
      ScannedDocumentList.list({
        params: { encounter: encounterId, patient: patientId },
        apiToken: controller.apiToken,
        extra: extraOf(controller),
      }),
    ),
    emrId ? softCall(() => DoctorCertificatesList.list({
      params: { emr: emrId, disable_pagination: true }, apiToken: controller.apiToken, extra: extraOf(controller),
    })) : Promise.resolve({ payload: emr.error ? PARTIAL_FAILED : undefined, failed: !!emr.error, errorText: emr.error }),
    /* ใบรับรองจากเมนูใบรับรองของ V3/V4 เก็บใน FormData ไม่ใช่ DoctorCertificates */
    softCall(() => DoctorCertificatesTypeList.list({
      apiToken: controller.apiToken, extra: extraOf(controller),
    })),
    softCall(() => FormDataActionLogAllList.list({
      params: { encounter: encounterId, limit: 9999 }, apiToken: controller.apiToken, extra: extraOf(controller),
    })),
    patientId
      ? softCall(() =>
          AdverseReactionList.list({
            params: allergyListParams(patientId),
            apiToken: controller.apiToken,
            extra: extraOf(controller),
          }),
        )
      : Promise.resolve({ payload: undefined, failed: false, errorText: null }),
    patientId
      ? softCall(() =>
          ProblemListList.list({
            params: problemListParams(patientId),
            apiToken: controller.apiToken,
            extra: extraOf(controller),
          }),
        )
      : Promise.resolve({ payload: undefined, failed: false, errorText: null }),
  ]);

  const parts: { key: string; res: { payload: unknown; failed: boolean; errorText: string | null } }[] = [
    { key: "emr", res: emrPayload },
    { key: "diagnosis", res: diagnosisPayload },
    { key: "triage", res: triagePayload },
    { key: "vitals", res: vitalPayload },
    { key: "labs", res: labPayload },
    { key: "imaging", res: imagingPayload },
    { key: "medications", res: medicationPayload },
    { key: "administration", res: administrationPayload },
    { key: "procedures", res: procedurePayload },
    { key: "nurseNotes", res: nursePayload },
    { key: "consultNotes", res: consultPayload },
    { key: "gallery", res: galleryPayload },
    { key: "scannedDocs", res: scannedPayload },
    {
      key: "certificates",
      res: [certificatePayload, certificateTypePayload, formCertificatePayload].find((res) => res.failed) ?? certificatePayload,
    },
    { key: "allergies", res: allergyPayload },
    { key: "problems", res: problemPayload },
  ];

  const firstFailure = parts.find((part) => part.res.failed);

  const payloads: HistoryDetailPayloads = {
    encounter: { ...encounter, emrId: emrId ?? null },
    noEmr: emrId === undefined && !emr.error,
    emr: emrPayload.payload,
    diagnosis: diagnosisPayload.payload,
    triage: triagePayload.payload,
    vitals: vitalPayload.payload,
    labs: labPayload.payload,
    imaging: imagingPayload.payload,
    medications: medicationPayload.payload,
    administration: administrationPayload.payload,
    procedures: procedurePayload.payload,
    nurseNotes: nursePayload.payload,
    consultNotes: consultPayload.payload,
    gallery: galleryPayload.payload,
    scannedDocs: scannedPayload.payload,
    certificates: certificatePayload.payload,
    certificateTypes: certificateTypePayload.payload,
    formCertificates: formCertificatePayload.payload,
    allergies: allergyPayload.payload,
    problems: problemPayload.payload,
  };

  if (stale()) return;
  const detail = historyDetailFromApiPayloads(payloads);

  writeSlice(controller, params, {
    ids: { ...ids, encounterId, emrId },
    selectedId: encounterId,
    detailFor: encounterId,
    /* model เป็นผู้ตรวจว่าส่วนไหนล้ม (softCall ส่ง PARTIAL_FAILED มาให้) */
    detail,
    detailLoading: false,
    /* emr ที่ resolve แล้ว ใช้ต่อในครั้งถัดไป */
    encounters: sliceOf(controller).encounters.map((row) => (row.id === encounterId ? { ...row, emrId: emrId ?? null } : row)),
    /* ถ้าส่วนใดส่วนหนึ่งล้ม: เก็บชื่อส่วนใน detail.partialErrors และยกข้อความ error แรกที่เจอ
       (แบบ 4d ยังแสดงส่วนอื่นของ encounter) */
    error: firstFailure ? firstFailure.res.errorText ?? "อ่านประวัติของ encounter นี้ไม่ครบทุกส่วน" : null,
  });
};

/* ============================================================== */
/*   SECTION 6 · SEQUENCE ENTRY / DISPATCH                         */
/* ============================================================== */

const LOAD_METAS: SliceMeta[] = [{ card: CARD_NAME, action: ACTIONS.LOAD }];

export const Start: Handler<SeqState> = async (controller, params) => {
  const ids = readIds(controller, params);
  const filter = filterOf(controller, params);

  controller.setState({
    PatientHistoryV4Sequence: { ...emptyHistoryV4Slice(), ids, filter, sequenceIndex: "Action" },
  });
  setBtn(controller, LOAD_METAS, BLC_STATUS.LOADING);

  await LoadEncounters(controller, { action: ACTIONS.LOAD, card: CARD_NAME, ids, filter });
};

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionName]: Handler<Ctx<K>> }> = {
    [ACTIONS.LOAD]: LoadEncounters,
    [ACTIONS.SET_FILTER]: SetFilter,
    [ACTIONS.SELECT]: SelectEncounter,
    [ACTIONS.LOAD_DETAIL]: LoadDetail,
  };

  const action = params.action as ActionName;
  const meta: SliceMeta = { card: (params as SliceMeta).card, action };
  const handler = actionHandlers[action] as Handler<Ctx<ActionName>> | undefined;

  if (handler) {
    return handler(controller, {
      ...params,
      action,
      ids: readIds(controller, params as HistoryIdsInput),
    } as Ctx<ActionName>);
  }

  console.error(`No handler found for action: ${String(action)}`);
  return writeSlice(controller, meta, {
    error: `ไม่รู้จัก action "${asText(action)}" ใน ${SEQUENCE_NAME}`,
  });
};

/* helper export ให้ test/UI ใช้ต่อ (กัน helper ตายเงียบ) */
export { todayNumOf };

/* GET only. The re-print route is not used here: its serializer reads printlogs.last().division,
   which answers HTTP 500 for a certificate that has never been printed. */
export const readHistoryCertificate = (id: number, apiToken?: string) =>
  DoctorCertificatesDetail.retrieve({ pk: id, apiToken, params: {} });

export const readHistoryFormCertificate = (id: number, apiToken?: string) =>
  FormDataDetail.retrieve({ pk: id, apiToken, params: {} });
